'use client';

import { useEffect, useMemo, useState } from 'react';

import { clearCategorias, listarCategorias } from '@/features/categoria/store';
import {
  buscarEscritorioPorId,
  buscarWorkspacePessoal,
  listarWorkspaces,
  updateLastWorkspace,
} from '@/features/escritorio/store/crud';
import {
  selectCurrentEscritorio,
  selectEscritorioList,
  selectEscritorioLoading,
  selectEscritorioTotal,
} from '@/features/escritorio/store/crud/selectors';
import {
  clearError as clearExtrajudicialError,
  listarNotificacoes,
  listarTemplates,
  obterEstatisticas,
} from '@/features/extrajudicial/store';
import { fetchHomeData } from '@/features/home/store';
import { fetchNegociacaoCasos } from '@/features/negociacoes/store';
import {
  escolherWorkspaceInicial,
  getStoredWorkspaceId,
  getImpersonatedTenantId,
  setStoredWorkspaceId,
} from '@/shared/utils/workspace';
import { listarProcessos } from '@/features/processos/store';
import { clearSetores, listarSetores } from '@/features/setor/store';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { CategoriaFilters } from '@/types/categoria/categoriaTypes';
import type { Escritorio } from '@/types/escritorio/escritorioCrudTypes';
import type { SetorFilters } from '@/types/setor/setorTypes';
import { loadAuthSession } from '@/features/auth/store';
import { selectAuthState } from '@/features/auth/store/selectors';

interface UseEscritorioSwitcherParams {
  limit?: number;
}

interface UseEscritorioSwitcherResult {
  selected: Escritorio | null;
  items: Escritorio[];
  loading: boolean;
  total: number;
  query: string;
  setQuery: (value: string) => void;
  favorites: string[];
  toggleFavorite: (id: string) => void;
  loadMore: () => void;
  createOpen: boolean;
  openCreateModal: () => void;
  closeCreateModal: () => void;
  handleCreateSuccess: () => Promise<void>;
  selectEscritorio: (id: string) => Promise<void>;
}

// Contador de módulo, não `useRef`: o hook roda em várias telas ao mesmo tempo (Sidebar,
// configurações, notificações) e a troca mais nova vale para todas.
let ultimaTrocaDeEscritorio = 0;

export const useEscritorioSwitcher = (
  params: UseEscritorioSwitcherParams = {},
): UseEscritorioSwitcherResult => {
  const { limit = 20 } = params;

  const dispatch = useAppDispatch();
  const list = useAppSelector(selectEscritorioList);
  const loading = useAppSelector(selectEscritorioLoading);
  const total = useAppSelector(selectEscritorioTotal);
  const current = useAppSelector(selectCurrentEscritorio);
  const { user } = useAppSelector(selectAuthState);

  const [query, setQuery] = useState('');
  const [items, setItems] = useState<Escritorio[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [limitCount, setLimitCount] = useState(limit);

  const selected = useMemo(() => current ?? null, [current]);

  const [favorites, setFavorites] = useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem('favWorkspaces');
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });

  const saveFavorites = (ids: string[]) => {
    setFavorites(ids);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('favWorkspaces', JSON.stringify(ids));
    }
  };

  const toggleFavorite = (id: string) => {
    const exists = favorites.includes(id);
    const next = exists ? favorites.filter((value) => value !== id) : [...favorites, id];
    saveFavorites(next);
  };

  useEffect(() => {
    if (!user) return;

    const loadWorkspaces = async () => {
      const workspacesResult = (await dispatch(listarWorkspaces())
        .unwrap()
        .catch(() => null)) as { data: Escritorio[]; total: number } | null;
      const workspaces = workspacesResult?.data ?? [];
      const fallbackWorkspaceId = workspaces.find((w) => w.ativo)?.id ?? workspaces[0]?.id ?? null;

      const storedId = escolherWorkspaceInicial({
        localStoredId: getStoredWorkspaceId(),
        lastWorkspaceId: user?.lastWorkspaceId,
        idsAcessiveis: workspacesResult ? new Set(workspaces.map((w) => w.id)) : null,
        tenantImpersonado: getImpersonatedTenantId(),
      });

      if (storedId) {
        setStoredWorkspaceId(String(storedId));

        await dispatch(buscarEscritorioPorId(storedId))
          .unwrap()
          .catch(async () => {
            const personal = (await dispatch(buscarWorkspacePessoal())
              .unwrap()
              .catch(() => null)) as Escritorio | null;

            const nextId = personal?.id ?? fallbackWorkspaceId;
            if (!nextId) {
              return;
            }

            setStoredWorkspaceId(String(nextId));

            await dispatch(buscarEscritorioPorId(String(nextId)))
              .unwrap()
              .catch(() => undefined);
          });
        return;
      }

      const personal = (await dispatch(buscarWorkspacePessoal())
        .unwrap()
        .catch(() => null)) as Escritorio | null;

      const nextId = personal?.id ?? fallbackWorkspaceId;
      if (nextId) {
        setStoredWorkspaceId(String(nextId));

        await dispatch(buscarEscritorioPorId(String(nextId)))
          .unwrap()
          .catch(() => undefined);
      }
    };

    void loadWorkspaces();
  }, [dispatch, user]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const base = list ?? [];
      const filtered = query
        ? base.filter((escritorio) =>
            escritorio.nomeFantasia.toLowerCase().includes(query.toLowerCase()),
          )
        : base;

      const favoriteItems = filtered.filter((escritorio) => favorites.includes(escritorio.id));
      const otherItems = filtered.filter((escritorio) => !favorites.includes(escritorio.id));

      setItems([...favoriteItems, ...otherItems].slice(0, limitCount));
    }, 200);

    return () => window.clearTimeout(timeoutId);
  }, [list, query, limitCount, favorites]);

  useEffect(() => {
    setItems(list ?? []);
  }, [list]);

  const loadMore = () => {
    setLimitCount((previous) => previous + limit);
  };

  const selectEscritorio = async (id: string) => {
    if (id === selected?.id) return;

    // Gravar antes de o Redux trocar o escritório: quem reage à troca (Sidebar, lista arbitral)
    // refaz as buscas na hora, e o `X-Workspace-Id` delas sai daqui. Na ordem inversa, o contrato
    // do escritório novo vinha com o do anterior e ficava assim até recarregar a página.
    const tentativa = ++ultimaTrocaDeEscritorio;
    const escritorioAntes = selected?.id ?? getStoredWorkspaceId();
    setStoredWorkspaceId(id);

    const escritorioConfirmado = await dispatch(buscarEscritorioPorId(id))
      .unwrap()
      .then(
        (escritorio) => escritorio.id,
        () => null,
      );

    // Outra troca começou enquanto esta esperava: ela é dona do header e das buscas. Sem isto, a
    // resposta atrasada de A dispararia as buscas abaixo com o `X-Workspace-Id` de B.
    if (tentativa !== ultimaTrocaDeEscritorio) return;

    // Falhou: o Redux segue no escritório anterior, então o header volta a ser o dele e nada
    // mais é buscado. Só desfaz se ninguém de fora deste hook trocou de novo no meio.
    if (escritorioConfirmado === null) {
      if (getStoredWorkspaceId() === id) setStoredWorkspaceId(escritorioAntes ?? '');
      return;
    }

    // O header segue o escritório que o back confirmou, não o id pedido. Regravar o mesmo valor
    // dispararia de novo o evento de troca e recarregaria os catálogos à toa.
    if (escritorioConfirmado !== id) setStoredWorkspaceId(escritorioConfirmado);

    dispatch(clearSetores());
    dispatch(clearCategorias());

    void dispatch(loadAuthSession());

    const isImpersonating = getImpersonatedTenantId() !== null;
    if (!isImpersonating && user) {
      dispatch(updateLastWorkspace({ userId: user.id, workspaceId: escritorioConfirmado }));
    }

    const setorFilters: SetorFilters = { page: 1, limit: 100 };
    const categoriaFilters: CategoriaFilters = { page: 1, limit: 100 };
    dispatch(listarSetores(setorFilters));
    dispatch(listarCategorias(categoriaFilters));
    dispatch(listarProcessos({ page: 1, limit: 10 }));
    dispatch(fetchHomeData());

    dispatch(clearExtrajudicialError());
    dispatch(listarTemplates());
    dispatch(listarNotificacoes({ page: 1, pageSize: 10 }));
    dispatch(obterEstatisticas());
    dispatch(fetchNegociacaoCasos({ page: 1, limit: 10 }));
  };

  const openCreateModal = () => {
    setCreateOpen(true);
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
  };

  const handleCreateSuccess = async () => {
    await dispatch(listarWorkspaces())
      .unwrap()
      .catch(() => undefined);
    setCreateOpen(false);
  };

  return {
    selected,
    items,
    loading,
    total,
    query,
    setQuery,
    favorites,
    toggleFavorite,
    loadMore,
    createOpen,
    openCreateModal,
    closeCreateModal,
    handleCreateSuccess,
    selectEscritorio,
  };
};
